import { Injectable } from '@angular/core';
import { ClaimsApiService } from '@ecosure-suite/shared/data-access';
import { LogService } from '@ecosure-suite/shared/util-logging';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ClaimLine,
  ClaimLineForm,
  ClaimRequest
} from '@ecosure-suite/shared/util-entities';
import { ClaimLinesConverterService } from '@ecosure-suite/shared/back-office/claims/utils-transformations';
import { Pagination } from '@ecosure-suite/shared/utils-helpers';

@Injectable({
  providedIn: 'root'
})
export class ClaimLinesService {
  constructor(
    private apiService: ClaimsApiService,
    private logService: LogService,
    private claimLineConverter: ClaimLinesConverterService
  ) {}

  get(query: {
    claimId: number;
    claimRequestId: number;
  }): Observable<ClaimLine[]> {
    return this.apiService.get(`/claims/${query.claimId}/claimlines`).pipe(
      map(data => {
        if (data.statusCode !== '200') {
          throw new Error(data.message);
        }
        return data.responseBody.map(claimLines =>
          this.claimLineConverter.convertClaimLine(
            claimLines,
            query.claimRequestId
          )
        );
      })
    );
  }

  getClaimLinesByCurrentUser({
    currentPage,
    itemsPerPage
  }: {
    currentPage: number;
    itemsPerPage: number;
  }): Observable<{
    claimLines: ClaimLine[];
    page: Partial<Pagination>;
  }> {
    return this.apiService
      .get(
        `/claimLines/authFilter?page=${currentPage -
          1}&fetchSize=${itemsPerPage}`
      )
      .pipe(
        map(data => {
          if (data.statusCode !== `200` && data.statusCode !== `404`) {
            throw new Error(data.message);
          }

          return {
            claimLines:
              data.statusCode === `404`
                ? []
                : (data.responseBody.elements.map(
                    this.claimLineConverter.convertClaimLine
                  ) as ClaimLine[]),
            page: {
              currentPage: data.responseBody.pageNumber + 1,
              totalItems: data.responseBody.totalElements,
              totalPages: data.responseBody.pageCount
            }
          };
        })
      );
  }

  listByStatusOrAssignee({
    currentPage,
    itemsPerPage,
    username,
    status
  }: {
    currentPage: number;
    itemsPerPage: number;
    username?: string;
    status?: string;
  }): Observable<{
    claimLines: (ClaimLine & { claimRequest: ClaimRequest })[];
    page: Partial<Pagination>;
  }> {
    return this.apiService
      .get(
        `/claimLines?page=${currentPage - 1}&fetchSize=${itemsPerPage}${
          username ? `&user=${username}` : ``
        }${status ? `&status=${status.replace(/ /g, '_')}` : ``}`
      )
      .pipe(
        map(data => {
          if (data.statusCode !== `200` && data.statusCode !== `404`) {
            throw new Error(data.message);
          }

          return {
            claimLines:
              data.statusCode === `404`
                ? []
                : data.responseBody.elements.map(item =>
                    Object.assign(
                      {},
                      this.claimLineConverter.convertClaimLine(
                        item.uiClaimLine,
                        item.uiClaimRequest.claimRequestId
                      ),
                      {
                        claimRequest: {
                          id: item.uiClaimRequest.claimRequestId,
                          status: item.uiClaimRequest.claimRequestStatus.replace(
                            /_/g,
                            ' '
                          ),
                          address: item.uiClaimRequest.policyMemberAddress,
                          dateCreated: new Date(
                            item.uiClaimRequest.creationDate
                          ),
                          createdBy: item.uiClaimRequest.createdBy,
                          firstName: item.uiClaimRequest.firstName,
                          lastName: item.uiClaimRequest.surname,
                          idNumber: item.uiClaimRequest.nationalId,
                          gender: item.uiClaimRequest.gender,
                          birthDate: new Date(item.uiClaimRequest.dateOfBirth),
                          msisdn: item.uiClaimRequest.msisdn,
                          assignedTo: item.uiClaimRequest.assignedTo
                        }
                      }
                    )
                  ),
            page: {
              currentPage: data.responseBody.pageNumber + 1,
              totalItems: data.responseBody.totalElements,
              totalPages: data.responseBody.pageCount
            }
          };
        })
      );
  }

  create(claimLine: ClaimLineForm): Observable<ClaimLine> {
    return this.apiService
      .post(
        `/claims/${claimLine.claimId}/claimlines`,
        this.claimLineConverter.getRequestBody(claimLine)
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return this.claimLineConverter.convertClaimLine(data.responseBody);
        })
      );
  }

  edit(claimLine: ClaimLineForm): Observable<ClaimLine> {
    return this.apiService
      .put(
        `/claims/${claimLine.claimId}/claimlines/${claimLine.id}`,
        this.claimLineConverter.getRequestBody(claimLine)
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return this.claimLineConverter.convertClaimLine(data.responseBody);
        })
      );
  }

  delete({ claimId, id }: Partial<ClaimLineForm>): Observable<ClaimLine> {
    return this.apiService.delete(`/claims/${claimId}/claimlines/${id}`).pipe(
      map(data => {
        if (data.statusCode !== '200') {
          throw new Error(data.message);
        }
        return {
          id: data.responseBody.id,
          cause: data.responseBody.cause,
          claimTypeId: data.responseBody.claimTypeId,
          claimId: data.responseBody.claimId,
          name: data.responseBody.cause.name,
          documentOwnerType: data.responseBody.documentOwnerType,
          creationDate: data.responseBody.creationDate,
          createdBy: data.responseBody.createdBy,
          assignedTo: data.responseBody.assignedTo,
          status: data.responseBody.status.replace(/_/g, ' '),
          type: data.responseBody.type
        };
      })
    );
  }

  submit(claimLineId: number): Observable<Partial<ClaimLine>> {
    return this.apiService
      .put(
        `/claimLines/${claimLineId}/events/submissions?processAllLines=false`
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return {
            id: claimLineId,
            status: data.responseBody.postEventStatus.replace(/_/g, ' ')
          };
        })
      );
  }

  verify({
    claimLineId,
    comment
  }: {
    claimLineId: number;
    comment: string;
  }): Observable<Partial<ClaimLine>> {
    return this.apiService
      .put(`/claimLines/${claimLineId}/events/verifications`, {
        comment: comment
      })
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return {
            id: claimLineId,
            status: data.responseBody.postEventStatus.replace(/_/g, ' ')
          };
        })
      );
  }

  approve({
    claimLineId,
    comment
  }: {
    claimLineId: number;
    comment: string;
  }): Observable<Partial<ClaimLine>> {
    return this.apiService
      .put(`/claimLines/${claimLineId}/events/approvals`, {
        comment: comment
      })
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return {
            id: claimLineId,
            status: data.responseBody.postEventStatus.replace(/_/g, ' ')
          };
        })
      );
  }

  return({
    claimLineId,
    comment
  }: {
    claimLineId: number;
    comment: string;
  }): Observable<Partial<ClaimLine>> {
    return this.apiService
      .put(`/claimLines/${claimLineId}/events/returns`, {
        comment: comment
      })
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return {
            id: claimLineId,
            status: data.responseBody.postEventStatus.replace(/_/g, ' ')
          };
        })
      );
  }

  reject({
    claimLineId,
    comment
  }: {
    claimLineId: number;
    comment: string;
  }): Observable<Partial<ClaimLine>> {
    return this.apiService
      .put(`/claimLines/${claimLineId}/events/rejections`, {
        comment: comment
      })
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return {
            id: claimLineId,
            status: data.responseBody.postEventStatus.replace(/_/g, ' ')
          };
        })
      );
  }

  assign({
    claimId,
    claimLineId,
    username
  }: {
    claimId: number;
    claimLineId: number;
    username: string;
  }): Observable<Partial<ClaimLine>> {
    return this.apiService
      .put(`/claims/${claimId}/claimlines/${claimLineId}/assign/${username}`)
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return {
            id: claimLineId,
            claimId: data.responseBody.claimId,
            assignedTo: data.responseBody.assignedTo
          };
        })
      );
  }
}
